import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';

import { Select } from 'primeng/select';
import { Pagination } from '@shared/api/models/pagination.model';
import { UtilizadorService } from '@core/services/domain-services';
import { Utilizador } from '@core/models/models';
import { CriarUtilizadorComponent } from './criar-utilizador/criar-utilizador.component';
import { EditarUtilizadorComponent } from './editar-utilizador/editar-utilizador.component';
import { ResetarSenhaDialogComponent } from './resetar-senha-dialog/resetar-senha-dialog.component';
import { ListaUtilizadoresComponent } from './lista-utilizadores/lista-utilizadores.component';
import { GerirGruposUtilizadorComponent } from './gerir-grupos-utilizador/gerir-grupos-utilizador.component';

export type UserTabType = 'todos' | 'ADMIN' | 'CLIENTE' | 'EFETIVO';

interface FiltroChip {
  key: string;
  label: string;
}

const FILTRO_LABELS: Record<string, string> = {
  Nome: 'Nome',
  UserId: 'User ID',
  Email: 'E-mail',
  UserName: 'NIF',
  TipoPerfilEnum: 'Perfil',
  IsActive: 'Estado',
};

@Component({
  selector: 'app-utilizadores',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,

    Select,
    CriarUtilizadorComponent,
    EditarUtilizadorComponent,
    ResetarSenhaDialogComponent,
    ListaUtilizadoresComponent,
    GerirGruposUtilizadorComponent,
  ],
  templateUrl: './utilizadores.component.html',
  styleUrl: './utilizadores.component.scss',
})
export class UtilizadoresComponent implements OnInit {
  activeTab: UserTabType = 'todos';
  filtrosPainelAberto = false;
  mostrarDrawerCriar = false;
  mostrarDrawerEditar = false;
  mostrarDialogReset = false;
  mostrarDialogGrupos = false;
  utilizadorSelecionado: Utilizador | null = null;
  skeletonLoading = false;
  totalCount = 0;
  totalCountEfetivo = 0;
  totalCountCliente = 0;
  totalCountAdmin = 0;

  filtrosForm!: FormGroup;
  pagination: Pagination<Utilizador> = new Pagination<Utilizador>();

  /** Últimos filtros aplicados (sem pageNumber/pageSize), para preservar na paginação. */
  private lastFiltros: Record<string, any> = {};

  perfilOptions = [
    { label: 'Efetivo', value: 'EFETIVO' },
    { label: 'Cliente', value: 'CLIENTE' },
    { label: 'Admin', value: 'ADMIN' },
  ];

  estadoOptions = [
    { label: 'Ativo', value: true },
    { label: 'Inativo', value: false },
  ];

  private readonly fb = inject(FormBuilder);
  private readonly userSvc = inject(UtilizadorService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.initFiltrosForm();
    this.loadUtilizadores();
    this.loadKpis();
  }

  private loadKpis(): void {
    forkJoin({
      efetivo: this.userSvc.getAll({
        TipoPerfilEnum: 'EFETIVO',
        pageNumber: 1,
        pageSize: 1,
      }),
      cliente: this.userSvc.getAll({
        TipoPerfilEnum: 'CLIENTE',
        pageNumber: 1,
        pageSize: 1,
      }),
      admin: this.userSvc.getAll({
        TipoPerfilEnum: 'ADMIN',
        pageNumber: 1,
        pageSize: 1,
      }),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.totalCountEfetivo =
            res.efetivo.totalRecords ?? (res.efetivo.data || []).length;
          this.totalCountCliente =
            res.cliente.totalRecords ?? (res.cliente.data || []).length;
          this.totalCountAdmin =
            res.admin.totalRecords ?? (res.admin.data || []).length;
          this.cdr.detectChanges();
        },
        error: (err) =>
          console.error('Erro ao carregar KPIs de utilizadores:', err),
      });
  }

  private initFiltrosForm(): void {
    this.filtrosForm = this.fb.group({
      Nome: [''],
      UserId: [''],
      Email: [''],
      UserName: [''],
      TipoPerfilEnum: [null],
      IsActive: [null],
    });
  }

  get activeFilterCount(): number {
    return Object.values(this.filtrosForm?.value ?? {}).filter(
      (v) => v !== null && v !== undefined && v !== '',
    ).length;
  }

  get filtroChips(): FiltroChip[] {
    return Object.entries(this.lastFiltros)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([key, value]) => ({
        key,
        label: `${FILTRO_LABELS[key] ?? key}: ${this.formatFiltroValue(key, value)}`,
      }));
  }

  private formatFiltroValue(key: string, value: any): string {
    if (key === 'IsActive') return value ? 'Ativo' : 'Inativo';
    if (key === 'TipoPerfilEnum') {
      return this.perfilOptions.find((p) => p.value === value)?.label ?? value;
    }
    return String(value);
  }

  removerFiltro(key: string): void {
    this.filtrosForm
      .get(key)
      ?.setValue(
        key === 'IsActive' ? null : key === 'TipoPerfilEnum' ? null : '',
      );
    this.aplicarFiltrosDrawer();
  }

  setActiveTab(tabId: UserTabType): void {
    this.activeTab = tabId;
    this.filtrosForm
      .get('TipoPerfilEnum')
      ?.setValue(tabId === 'todos' ? null : tabId, { emitEvent: false });
    this.aplicarFiltros();
  }

  limparFiltros(): void {
    this.filtrosForm.reset();
    this.activeTab = 'todos';
  }

  aplicarFiltrosDrawer(): void {
    this.filtrosPainelAberto = false;
    this.aplicarFiltros();
  }

  private aplicarFiltros(): void {
    const val = this.filtrosForm.value;
    const params: Record<string, any> = {};
    Object.entries(val).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') {
        params[k] = v;
      }
    });

    // Sincronizar a tab ativa com o perfil escolhido no drawer
    if (params['TipoPerfilEnum']) {
      this.activeTab = params['TipoPerfilEnum'];
    } else {
      this.activeTab = 'todos';
    }

    this.lastFiltros = params;
    this.loadUtilizadores({ ...params, pageNumber: 1 });
  }

  loadUtilizadores(queryParams: Record<string, any> = {}): void {
    this.skeletonLoading = true;
    this.cdr.detectChanges();

    const params: Record<string, any> = {
      pageNumber: queryParams['pageNumber'] ?? 1,
      pageSize: queryParams['pageSize'] ?? 10,
      ...queryParams,
    };

    this.userSvc
      .getAll(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.pagination = new Pagination<Utilizador>().deserialize(res);
          this.totalCount = res.totalRecords ?? (res.data || []).length;
          this.skeletonLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Falha ao carregar utilizadores:', err);
          this.skeletonLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  onPageChange(event: any): void {
    const pageIndex = event.first / event.rows + 1;
    this.loadUtilizadores({
      ...this.lastFiltros,
      pageNumber: pageIndex,
      pageSize: event.rows,
    });
  }

  criarUtilizador(): void {
    this.mostrarDrawerCriar = true;
  }

  onEditar(user: Utilizador): void {
    this.utilizadorSelecionado = user;
    this.mostrarDrawerEditar = true;
  }

  onResetarSenha(user: Utilizador): void {
    this.utilizadorSelecionado = user;
    this.mostrarDialogReset = true;
  }

  onGerirGrupos(user: Utilizador): void {
    this.utilizadorSelecionado = user;
    this.mostrarDialogGrupos = true;
  }

  /** Recarrega mantendo a página e os filtros atuais (usado após editar/resetar senha). */
  reloadCurrentPage(): void {
    this.loadUtilizadores({
      ...this.lastFiltros,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 10,
    });
  }
}
